import { useCallback, useMemo } from 'react';

import type { ActiveItem } from './useActiveItem';
import { useActiveItemStore } from './useActiveItem';

//* We work with { [key: string]: boolean } object to work with react-table v8

type SelectedDocument = {
  [key: string]: boolean;
};

type ToggleSelectionOptions = {
  keepExistingSelection?: boolean;
};

export const useDocumentSelection = () => {
  const {
    activeItems,
    setActiveItemsByType,
    toggleActiveItems,
    removeActiveItems,
    pruneActiveItems,
  } = useActiveItemStore();

  const selectedDocumentIds = useMemo(
    () => activeItems.filter(({ type }) => type === 'document').map(({ id }) => id),
    [activeItems],
  );
  const selectedDocuments = useMemo<SelectedDocument>(
    () => Object.fromEntries(selectedDocumentIds.map((id) => [id, true] as const)),
    [selectedDocumentIds],
  );

  const handleDocumentSelection = useCallback(
    (selection: SelectedDocument | string) => {
      const activeItems: Array<ActiveItem> =
        typeof selection === 'string'
          ? [{ id: selection, type: 'document' }]
          : Object.keys(selection).map((id) => ({ id, type: 'document' }));
      setActiveItemsByType({ type: 'document', activeItems });
    },
    [setActiveItemsByType],
  );

  const isSelected = useCallback(
    (id: string) => Boolean(selectedDocuments[id]),
    [selectedDocuments],
  );

  const toggleDocumentSelection = useCallback(
    (id: string, { keepExistingSelection }: ToggleSelectionOptions = {}) => {
      const activeItems: Array<ActiveItem> = [{ id, type: 'document' }];
      toggleActiveItems({ activeItems, keepExistingSelection });
    },
    [toggleActiveItems],
  );

  const setDocumentsIds = useCallback(
    (ids: string[]) => {
      setActiveItemsByType({
        type: 'document',
        activeItems: ids.map((id) => ({ id, type: 'document' })),
      });
    },
    [setActiveItemsByType],
  );

  const removeDocumentSelection = useCallback(
    (ids: string[]) => {
      removeActiveItems({ ids, type: 'document' });
    },
    [removeActiveItems],
  );

  const pruneDocumentSelection = useCallback(
    (visibleIds: string[]) => {
      pruneActiveItems({ visibleIds, type: 'document' });
    },
    [pruneActiveItems],
  );

  const clearDocumentSelection = useCallback(() => {
    setActiveItemsByType({ type: 'document', activeItems: [] });
  }, [setActiveItemsByType]);

  return {
    activeDocumentId: selectedDocumentIds[0],
    selectedDocuments,
    selectedDocumentIds,
    hasMultipleSelected: selectedDocumentIds.length > 1,
    numberOfDocumentsSelected: selectedDocumentIds.length,
    toggleDocumentSelection,
    setDocumentsIds,
    handleDocumentSelection,
    clearDocumentSelection,
    removeDocumentSelection,
    pruneDocumentSelection,
    isSelected,
  };
};
